import { useQueryClient } from '@tanstack/react-query'
import { Form } from 'antd'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useNotificationsServiceGetTemplateEndpoint,
  useNotificationsServiceGetTemplateEndpointKey,
  useNotificationsServiceGetTemplatesListEndpointKey,
  useNotificationsServiceUpdateTemplateEndpoint
} from 'src/api/queries'
import { ApiError, SystemLanguage } from 'src/api/requests'
import { PageBreadcrumbs } from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { CreateNotificationCard } from '../components'

export const NotificationTemplatesEditPage = () => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data } = useNotificationsServiceGetTemplateEndpoint(
    {
      id: String(id)
    },
    [id],
    { enabled: !!id }
  )

  const { mutate, isPending } = useNotificationsServiceUpdateTemplateEndpoint({
    onSuccess: () => onMutationSuccess(),
    onError: (e: ApiError) => onMutationError(e)
  })

  const onSubmit = async () => {
    await form.validateFields()

    const values = form.getFieldsValue(true)

    const requestBody = {
      ...values,
      contents: [
        {
          languageId: SystemLanguage.RO,
          title: values?.title_ro,
          content: values?.content_ro
        },
        {
          languageId: SystemLanguage.RU,
          title: values?.title_ru,
          content: values?.content_ru
        }
      ]
    }

    mutate({ requestBody, id: String(id) })
  }

  const onCancel = () => {
    form.resetFields()
    navigate(paths.notificationTemplates.root)
  }

  const onMutationSuccess = () => {
    navigate(paths.notificationTemplates.root)
    queryClient.invalidateQueries({
      queryKey: [useNotificationsServiceGetTemplatesListEndpointKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useNotificationsServiceGetTemplateEndpointKey, String(id)]
    })
  }

  const onMutationError = (e: ApiError) => {
    form?.setFields(formErrorHandler(e.body.errors))
  }

  useEffect(() => {
    const roContent = data?.contents?.find(
      (item) => item.languageId === SystemLanguage.RO
    )

    const ruContent = data?.contents?.find(
      (item) => item.languageId === SystemLanguage.RU
    )

    form.setFieldsValue({
      ...data,
      title_ro: roContent?.title,
      content_ro: roContent?.content,
      title_ru: ruContent?.title,
      content_ru: ruContent?.content
    })
  }, [data])

  return (
    <PageBreadcrumbs
      title={t('notification_templates.edit_template')}
      items={[
        {
          title: t('menu.notification_templates'),
          path: paths.notificationTemplates.root
        },
        {
          title: t('notification_templates.edit_template')
        }
      ]}
    >
      <CreateNotificationCard
        isPending={isPending}
        form={form}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    </PageBreadcrumbs>
  )
}
